import { useAddConfigMutation, useUpdateConfigMutation } from '../store/modules/merchant/api';
import { ConfigResponse } from '../store/modules/merchant/types';
import { isServerError } from '../util/helpers';

export type ConfigToUpsert = {
  name: ConfigResponse['name'];
  value: ConfigResponse['value'];
  enabled?: boolean;
} & Partial<ConfigResponse>;

/**
 * This is a generic hook that can be used to update a config, or create it if it doesn't exist.
 * There is already a more specific hook for updating a config, but it handles some additional
 * logic that is not needed here.
 */
const useConfigUpsert = (merchantId: string) => {
  const [addConfig, { isLoading: isAdding }] = useAddConfigMutation();
  const [updateConfig, { isLoading: isUpdating }] = useUpdateConfigMutation();

  const upsertOneConfig = async (config: ConfigToUpsert) => {
    const { name, value, enabled, configId, merchantId: configMerchantId } = config;
    const isInherited = configMerchantId && (configMerchantId !== merchantId || config.isInherited);
    if (configId && !isInherited) {
      await updateConfig({
        merchantId,
        name,
        configId,
        value,
        enabled,
      }).unwrap();
    } else {
      await addConfig({
        merchantId,
        name,
        value,
        enabled: enabled ?? true,
      }).unwrap();
    }
  };

  const onConfigUpsertBatch = async (configs: ConfigToUpsert[]) => {
    for (const config of configs) {
      try {
        await upsertOneConfig(config);
      } catch (err) {
        if (isServerError(err) && err.data.statusCode === 400) {
          throw new Error(err.data.message);
        }
        throw err;
      }
    }
  };

  const onConfigUpsert = async (config: ConfigToUpsert) => {
    await onConfigUpsertBatch([config]);
  };

  return {
    onConfigUpsert,
    onConfigUpsertBatch,
    isLoading: isAdding || isUpdating,
  };
};

export { useConfigUpsert };
